import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ArrowRight, Loader2, ShieldCheck, Copy, AlertTriangle, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, mfaChallenge, enrollComplete } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // MFA challenge (already enrolled) state
  const [mfaTicket, setMfaTicket] = useState(null);
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  // MFA enrollment (forced first-login) state
  const [enrollTicket, setEnrollTicket] = useState(null);
  const [enrollData, setEnrollData] = useState(null); // { secret, qr_png, otpauth_uri, account }
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollBackupCodes, setEnrollBackupCodes] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfaTicket(result.mfa_ticket);
        setMfaEmail(result.email);
      } else if (result?.mfa_enrollment_required) {
        // Force the user through the MFA enrollment before anything else.
        setEnrollTicket(result.mfa_enroll_ticket);
        setMfaEmail(result.email);
        // Bootstrap the QR immediately
        try {
          const r = await api.post("/auth/mfa/enroll-init",
            { mfa_enroll_ticket: result.mfa_enroll_ticket });
          setEnrollData(r.data);
        } catch (e2) {
          setErr(e2?.response?.data?.detail || "No se pudo iniciar el enrollment de MFA");
          setEnrollTicket(null);
        }
      } else {
        nav("/dashboard", { replace: true });
      }
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      setErr(detail || (status === 423 ? "Cuenta bloqueada" : t("login.failed")));
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await mfaChallenge(mfaTicket, mfaCode);
      nav("/dashboard", { replace: true });
    } catch (ex) {
      if (ex?.mfa_reset_required) {
        // Server invalidated the stale MFA — bounce user back to login so the
        // next attempt routes them through the QR enrollment automatically.
        setMfaTicket(null); setMfaCode(""); setPassword("");
        toast.info("Tu MFA necesita reconfiguración. Vuelve a iniciar sesión para escanear un nuevo QR.");
        setErr("");
      } else {
        setErr(ex?.response?.data?.detail || "Código MFA inválido");
      }
    } finally { setBusy(false); }
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await enrollComplete(enrollTicket, enrollCode);
      setEnrollBackupCodes(r.backup_codes || []);
      toast.success("MFA activado");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  const finishEnroll = () => {
    setEnrollTicket(null); setEnrollData(null); setEnrollBackupCodes(null);
    nav("/dashboard", { replace: true });
  };

  const brandPanel = (
    <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-border relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-sev-critical/5" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <img src="/assets/cibraxis-logo.png" alt="CIBRAXIS"
               className="w-14 h-14 object-contain" data-testid="cibraxis-logo" />
          <div>
            <div className="font-display text-2xl font-bold tracking-tight leading-none">CIBRAXIS</div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase mt-1">By Ciberconsulting</div>
          </div>
        </div>
      </div>
      <div className="relative">
        <h1 className="font-display font-bold tracking-tighter leading-[0.95]" style={{ fontSize: "clamp(48px, 6vw, 88px)" }}>
          {t("login.headline_1")} <span className="text-primary">{t("login.headline_2")}</span><br />
          {t("login.headline_3")}
        </h1>
      </div>
      <div className="relative grid grid-cols-3 gap-4 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
        <div><span className="text-primary">01</span> {t("login.step_1")}</div>
        <div><span className="text-primary">02</span> {t("login.step_2")}</div>
        <div><span className="text-primary">03</span> {t("login.step_3")}</div>
      </div>
    </div>
  );

  // ---- MFA enrollment (forced) UI ------------------------------------
  if (enrollTicket) {
    return (
      <div className="min-h-screen flex" data-testid="login-page">
        {brandPanel}
        <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
          <div className="absolute top-4 right-4"><LanguageToggle /></div>

          {!enrollBackupCodes ? (
            <form onSubmit={submitEnroll} className="w-full max-w-lg" data-testid="mfa-enroll-form">
              <div className="overline mb-2 flex items-center gap-2 text-primary">
                <ShieldCheck className="w-3 h-3" /> Paso obligatorio · Activar MFA
              </div>
              <h2 className="font-display text-3xl font-medium mb-2">Configura tu segundo factor</h2>
              <p className="text-xs text-muted-foreground mb-6 font-mono">
                Antes de acceder, debes vincular tu cuenta <span className="text-foreground">{mfaEmail}</span> a{" "}
                <span className="text-foreground">Microsoft Authenticator</span> o{" "}
                <span className="text-foreground">Google Authenticator</span>. Este paso solo se hace una vez.
              </p>

              {enrollData ? (
                <div className="space-y-4">
                  <ol className="text-xs text-muted-foreground font-mono list-decimal list-inside space-y-1">
                    <li>Abre <span className="text-foreground inline-flex items-center gap-1"><Smartphone className="w-3 h-3" /> Google/Microsoft Authenticator</span>.</li>
                    <li>Toca <span className="text-foreground">+</span> y escanea este código QR.</li>
                    <li>Introduce debajo el código de 6 dígitos que genere la app.</li>
                  </ol>

                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4 items-start">
                    <div className="border border-border bg-white p-2 w-fit">
                      <img src={enrollData.qr_png} alt="QR MFA" className="w-44 h-44" data-testid="mfa-enroll-qr" />
                    </div>
                    <div className="space-y-2">
                      <div className="overline">Secreto (manual si no puedes escanear)</div>
                      <div className="font-mono text-xs bg-background border border-border p-2 flex items-center justify-between gap-2">
                        <span data-testid="mfa-enroll-secret" className="break-all">{enrollData.secret}</span>
                        <button type="button"
                                onClick={() => { navigator.clipboard.writeText(enrollData.secret); toast.success("Secreto copiado"); }}
                                className="p-1 hover:bg-muted"><Copy className="w-3 h-3" /></button>
                      </div>
                    </div>
                  </div>

                  <label className="block">
                    <div className="overline mb-1">Código de 6 dígitos</div>
                    <input inputMode="numeric" autoComplete="one-time-code" autoFocus
                           value={enrollCode}
                           onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                           placeholder="000000"
                           className="w-full bg-background border border-border px-3 py-3 text-2xl font-mono tracking-[0.5em] text-center focus:border-primary outline-none"
                           data-testid="mfa-enroll-code-input" required />
                  </label>

                  {err && (
                    <div className="border border-sev-critical/50 bg-sev-critical/10 text-sev-critical px-3 py-2 text-xs font-mono" data-testid="mfa-enroll-error">
                      {err}
                    </div>
                  )}
                  <button type="submit" disabled={busy || enrollCode.length !== 6}
                          className="w-full bg-primary text-primary-foreground px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50 font-medium"
                          data-testid="mfa-enroll-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Activar y entrar <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Generando código QR…
                </div>
              )}
            </form>
          ) : (
            <div className="w-full max-w-lg" data-testid="mfa-enroll-backup-codes">
              <div className="overline mb-2 flex items-center gap-2 text-sev-medium">
                <AlertTriangle className="w-3 h-3" /> Guarda estos códigos de respaldo
              </div>
              <h2 className="font-display text-3xl font-medium mb-2">Códigos de respaldo</h2>
              <p className="text-xs text-muted-foreground mb-4 font-mono">
                Si pierdes tu móvil, cada código funciona <span className="text-foreground">una sola vez</span> en lugar del TOTP.
                No se volverán a mostrar. Guárdalos en un gestor de contraseñas.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-xs mb-4">
                {enrollBackupCodes.map(c => (
                  <div key={c} className="bg-background border border-border p-2 text-center">{c}</div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(enrollBackupCodes.join("\n")); toast.success("Códigos copiados"); }}
                        className="text-xs font-mono tracking-widest uppercase border border-border px-3 py-2 hover:border-primary hover:text-primary inline-flex items-center gap-1"
                        data-testid="mfa-enroll-copy-codes">
                  <Copy className="w-3 h-3" /> Copiar todos
                </button>
                <button onClick={finishEnroll}
                        className="flex-1 bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 inline-flex items-center justify-center gap-2"
                        data-testid="mfa-enroll-continue">
                  Entendido, entrar al panel <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- MFA challenge (already enrolled) UI --------------------------
  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {brandPanel}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
        <div className="absolute top-4 right-4"><LanguageToggle /></div>

        {!mfaTicket ? (
          <form onSubmit={submit} className="w-full max-w-sm">
            <div className="overline mb-2">{t("login.overline")}</div>
            <h2 className="font-display text-3xl font-medium mb-8">{t("login.title")}</h2>
            <label className="block mb-4">
              <div className="overline mb-1">{t("login.email")}</div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     className="w-full bg-background border border-border px-3 py-2.5 text-sm font-mono focus:border-primary outline-none"
                     data-testid="login-email" required />
            </label>
            <label className="block mb-6">
              <div className="overline mb-1">{t("login.password")}</div>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     className="w-full bg-background border border-border px-3 py-2.5 text-sm font-mono focus:border-primary outline-none"
                     data-testid="login-password" required />
            </label>
            {err && (
              <div className="mb-4 border border-sev-critical/50 bg-sev-critical/10 text-sev-critical px-3 py-2 text-xs font-mono" data-testid="login-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy}
                    className="w-full bg-primary text-primary-foreground px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50 font-medium"
                    data-testid="login-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login.submit")} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={submitMfa} className="w-full max-w-sm" data-testid="mfa-challenge-form">
            <div className="overline mb-2 flex items-center gap-2 text-primary">
              <ShieldCheck className="w-3 h-3" /> Segundo factor requerido
            </div>
            <h2 className="font-display text-3xl font-medium mb-2">Introduce tu código</h2>
            <p className="text-xs text-muted-foreground mb-8 font-mono">
              Abre tu app autenticadora (Google/Microsoft Authenticator, Authy, etc.) e introduce el código de 6 dígitos generado para <span className="text-foreground">{mfaEmail}</span>.
            </p>
            <label className="block mb-6">
              <div className="overline mb-1">Código de 6 dígitos</div>
              <input inputMode="numeric" autoComplete="one-time-code" autoFocus
                     value={mfaCode}
                     onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     placeholder="000000"
                     className="w-full bg-background border border-border px-3 py-3 text-2xl font-mono tracking-[0.5em] text-center focus:border-primary outline-none"
                     data-testid="mfa-code-input" required />
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                También aceptamos códigos de respaldo (formato XXXX-XXXX).
              </div>
            </label>
            {err && (
              <div className="mb-4 border border-sev-critical/50 bg-sev-critical/10 text-sev-critical px-3 py-2 text-xs font-mono" data-testid="mfa-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy || (mfaCode.length !== 6 && mfaCode.length !== 9)}
                    className="w-full bg-primary text-primary-foreground px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50 font-medium"
                    data-testid="mfa-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verificar <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={() => { setMfaTicket(null); setMfaCode(""); setErr(""); }}
                    className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground font-mono tracking-widest uppercase">
              ← Volver a inicio de sesión
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
